import type { AttachmentAdapter, CompleteAttachment, PendingAttachment } from '@assistant-ui/react';
import {
  ALLOWED_MIMES,
  type AllowedUploadMime,
  MAX_IMAGES_PER_REQUEST,
  MAX_REQUEST_BYTES,
  MAX_UPLOAD_BYTES_IMAGE,
  MAX_UPLOAD_BYTES_PDF,
} from '@iq/contracts/v1/upload';
import { env } from './env';

/**
 * Live snapshot of the per-request attachment usage at the moment a new
 * attachment is being added. The adapter closes over this to validate
 * combined-bytes + image-count caps without calling the server first.
 *
 * `composer*` reflects pending composer attachments in the active AUI
 * instance; `sticky*` reflects thread-bound uploads listed via the
 * `GET /uploads?threadId=` query.
 */
export interface AttachmentUsage {
  composerBytes(): number;
  composerImageCount(): number;
  stickyBytes(): number;
  stickyImageCount(): number;
}

export interface UploadAdapterDeps {
  /** Active thread id; null when the user is on /chat with no thread bound. */
  threadId: () => string | null;
  /** Live composer + sticky-thread usage snapshot. */
  usage: AttachmentUsage;
  /**
   * i18n translator. Pass `(key, vars) => string`. The adapter returns
   * localized error messages directly to the runtime, which re-emits them
   * as `composer.attachmentAddError` with `reason: 'adapter-error'`. The
   * toast component consumes the message verbatim.
   */
  t: (key: string, vars?: Record<string, string | number>) => string;
  /** Optional fetch override for tests. */
  fetchImpl?: typeof fetch;
}

/**
 * Build the assistant-ui `AttachmentAdapter` that the chat runtime calls
 * for every drag/drop or file-picker selection.
 *
 * The async-iterator `add()` shape streams multiple status snapshots into
 * the composer (uploading → requires-action) in one adapter call. Pre-
 * validation against per-file + combined-request caps short-circuits the
 * upload when the user has already exceeded a cap, so an over-size file
 * never issues a POST that the server would 413.
 *
 * The trip to `/uploads` is one multipart POST; chunked provider upload,
 * dedupe, and DB insert all happen server-side. The server response is
 * `{id, status, ...}`; the server id is handed back through the final
 * yield so subsequent chat submits carry the real `upload_id`.
 */
export function createUploadAdapter(deps: UploadAdapterDeps): AttachmentAdapter {
  const fetchFn = deps.fetchImpl ?? fetch;
  // Closure map: frontend uuid → server upload_id.
  //
  // assistant-ui keys composer attachments by `attachment.id` and treats two
  // yields with different ids as two separate attachments, so the frontend
  // uuid stays stable across every yield. The server-issued id is surfaced
  // only at wire-shaping time (`send` writes it into the file-part `data`
  // field, `remove` uses it for the DELETE URL).
  const serverIdByLocalId = new Map<string, string>();

  return {
    accept: ALLOWED_MIMES.join(','),

    async *add({ file }: { file: File }): AsyncGenerator<PendingAttachment, void> {
      const id = crypto.randomUUID();
      const isPdf = file.type === 'application/pdf';
      const perFileCap = isPdf ? MAX_UPLOAD_BYTES_PDF : MAX_UPLOAD_BYTES_IMAGE;
      const kind: 'pdf' | 'image' = isPdf ? 'pdf' : 'image';
      const attachmentType: 'document' | 'image' = isPdf ? 'document' : 'image';
      const contentType = file.type as AllowedUploadMime;

      const errYield: PendingAttachment = {
        id,
        type: attachmentType,
        name: file.name,
        file,
        contentType,
        status: { type: 'incomplete', reason: 'error' },
      };

      // Pre-validate per-file size against per-mime cap.
      //
      // Throw WITHOUT yielding first. assistant-ui's `addAttachment` only
      // plants a composer chip for attachments it has already seen via a
      // yield (`lastAttachment`); a bare throw leaves `lastAttachment`
      // undefined so the `catch` skips the upsert entirely. Net effect:
      // an over-cap file fires the `composer.attachmentAddError` toast but
      // never attaches.
      if (file.size > perFileCap) {
        throw new Error(
          deps.t('chat.attachment.errors.fileTooLarge', {
            name: file.name,
            limitMB: Math.floor(perFileCap / 1_000_000),
          }),
        );
      }

      // Pre-validate combined-bytes (composer + sticky + this file) against the request cap.
      const composerBytes = deps.usage.composerBytes();
      const stickyBytes = deps.usage.stickyBytes();
      const projectedBytes = composerBytes + stickyBytes + file.size;
      if (projectedBytes > MAX_REQUEST_BYTES) {
        // Throw without yielding — toast only, no orphan chip (see above).
        throw new Error(
          deps.t('chat.attachment.errors.sizeOverflow', {
            currentMB: ((composerBytes + stickyBytes) / 1_000_000).toFixed(1),
            incomingMB: (file.size / 1_000_000).toFixed(1),
            limitMB: MAX_REQUEST_BYTES / 1_000_000,
          }),
        );
      }

      // Pre-validate image count (composer + sticky + this if image).
      if (kind === 'image') {
        const composerImages = deps.usage.composerImageCount();
        const stickyImages = deps.usage.stickyImageCount();
        const projectedImages = composerImages + stickyImages + 1;
        if (projectedImages > MAX_IMAGES_PER_REQUEST) {
          // Throw without yielding — toast only, no orphan chip (see above).
          throw new Error(
            deps.t('chat.attachment.errors.imageCountOverflow', {
              current: composerImages + stickyImages,
              incoming: 1,
              limit: MAX_IMAGES_PER_REQUEST,
            }),
          );
        }
      }

      // Yield uploading state (assistant-ui shows a spinner).
      yield {
        id,
        type: attachmentType,
        name: file.name,
        file,
        contentType,
        status: { type: 'running', reason: 'uploading', progress: 0 },
      };

      // POST to /api/v1/uploads. Server handles chunked provider upload + dedupe.
      const form = new FormData();
      form.append('file', file);
      const threadId = deps.threadId();
      const path = threadId
        ? `${env.VITE_API_BASE_URL}/uploads?threadId=${encodeURIComponent(threadId)}`
        : `${env.VITE_API_BASE_URL}/uploads`;
      const res = await fetchFn(path, {
        method: 'POST',
        credentials: 'include',
        headers: {
          // Idempotency-Key dedupes multi-tab + double-click submits at the
          // server's idempotency middleware.
          'Idempotency-Key': crypto.randomUUID(),
        },
        body: form,
      });

      if (!res.ok) {
        let body = '';
        try {
          body = await res.text();
        } catch {
          // Ignore read errors; the status code drives the message.
        }
        let message: string;
        if (res.status === 415) {
          message = deps.t('chat.attachment.errors.unsupportedType', { name: file.name });
        } else if (res.status === 413) {
          message = deps.t('chat.attachment.errors.fileTooLarge', {
            name: file.name,
            limitMB: Math.floor(perFileCap / 1_000_000),
          });
        } else if (res.status === 429) {
          message = deps.t('chat.attachment.errors.rateLimit');
        } else if (res.status === 502) {
          message = deps.t('chat.attachment.errors.providerOutage');
        } else {
          message = deps.t('chat.attachment.errors.uploadFailed', { status: res.status });
        }
        yield errYield;
        throw new Error(body ? `${message} (${body.slice(0, 200)})` : message);
      }

      const json = (await res.json()) as { id: string; status: string };
      // Persist the server id under the stable frontend uuid for `send`
      // and `remove` to resolve. Do NOT mutate `attachment.id` between
      // yields — assistant-ui treats an id change as a new attachment and
      // orphans the old chip in the composer.
      serverIdByLocalId.set(id, json.id);
      yield {
        id,
        type: attachmentType,
        name: file.name,
        file,
        contentType,
        status: { type: 'requires-action', reason: 'composer-send' },
      };
    },

    async send(attachment: PendingAttachment): Promise<CompleteAttachment> {
      // Server already has the file. Emit the completion + content
      // descriptor that assistant-ui's persistence hands to the runtime
      // when the message is sent. The SERVER-issued upload_id (looked up
      // from the closure map by the stable frontend uuid) is stashed in
      // the `data` field; assistant-ui's `toCreateMessage` maps that to
      // `part.url`, and `prepareSendMessagesRequest` extracts it back out
      // into the top-level `body.attachments` array the server expects.
      //
      // The file bytes are deliberately NOT embedded (data-URL or base64):
      // the chat send carries refs, not bytes, to stay under the request
      // body-size cap.
      const mimeType = attachment.contentType ?? 'application/octet-stream';
      // Fall back to the local id if the server id was never recorded
      // (`add` sets the entry on 2xx); the chat-handler then silently
      // drops an unrecognized ref via the audit log.
      const serverId = serverIdByLocalId.get(attachment.id) ?? attachment.id;
      return {
        id: attachment.id,
        type: attachment.type,
        name: attachment.name,
        contentType: attachment.contentType,
        status: { type: 'complete' },
        content: [
          {
            type: 'file',
            data: serverId,
            mimeType,
            filename: attachment.name,
          },
        ],
      };
    },

    async remove(attachment): Promise<void> {
      // Hit the server with the server-issued id, falling back to the
      // local id (only safe when `add` never round-tripped — i.e. user
      // removed a chip mid-upload).
      const serverId = serverIdByLocalId.get(attachment.id) ?? attachment.id;
      serverIdByLocalId.delete(attachment.id);
      await fetchFn(`${env.VITE_API_BASE_URL}/uploads/${serverId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
    },
  };
}
